export const analysisEn = {
  title: 'Analysis and nutrition profiles',
  subtitle:
    'Versioned laboratory and calculated values with provenance, controlled precedence, and approval.',
  help: { open: 'Open help for analysis values, calculations, and approvals' },
  pagination: 'Page {{page}} of {{pages}} · {{total}} versions',
  actions: {
    parameters: 'Analysis parameters',
    newParameter: 'New parameter',
    newLaboratory: 'Record laboratory profile',
    calculate: 'Calculate recipe',
    addValue: 'Add value',
    submit: 'Submit for review',
    review: 'Technical review',
    approve: 'Approve',
    copy: 'New version',
    archive: 'Archive',
    history: 'Version history',
  },
  filters: {
    searchProfiles: 'Search number, product, or recipe',
    searchParameters: 'Search key or name',
    domain: 'Domain, e.g. NUTRITION',
    allStatuses: 'All statuses',
    allOrigins: 'All source types',
  },
  statuses: {
    DRAFT: 'Draft',
    IN_REVIEW: 'In review',
    APPROVED: 'Approved',
    SUPERSEDED: 'Superseded',
    ARCHIVED: 'Archived',
  },
  origins: {
    ANALYZED: 'Analyzed',
    CALCULATED: 'Calculated',
    MIXED: 'Laboratory value takes precedence',
  },
  targetTypes: { PRODUCT: 'Product', RECIPE: 'Recipe' },
  issueSeverity: { ERROR: 'Error', WARNING: 'Warning' },
  aggregationModes: {
    MASS_BALANCE: 'Mass balance / additive quantity',
    WEIGHTED_AVERAGE: 'Weighted average',
    NOT_CALCULABLE: 'Not automatically calculable',
  },
  roundingModes: { HALF_UP: 'Half up', DOWN: 'Truncate', UP: 'Away from zero' },
  columns: {
    number: 'Number',
    target: 'Product / recipe',
    domain: 'Domain',
    origin: 'Origin',
    reference: 'Reference quantity',
    version: 'Version',
    status: 'Status',
    issues: 'Errors / warnings',
    key: 'Key',
    name: 'Name',
    parameter: 'Parameter',
    effective: 'Approved value',
    calculated: 'Calculated',
    analyzed: 'Analyzed',
    range: 'Min / target / max / tolerance',
    difference: 'Difference',
    hash: 'Snapshot hash',
    created: 'Created',
    validity: 'Validity',
  },
  fields: {
    key: 'Stable key',
    domain: 'Configurable domain',
    defaultUnit: 'Value unit',
    reference: 'Reference quantity and unit',
    referenceQuantity: 'Reference quantity',
    referenceUnit: 'Reference unit',
    aggregation: 'Calculation method',
    retention: 'Retention factor',
    rounding: 'Decimals / rounding',
    metadata: 'Configurable metadata (JSON)',
    changeReason: 'Reason for change',
    validFrom: 'Valid from',
    validTo: 'Valid until',
    name: 'Name',
    shortName: 'Short name',
    description: 'Description',
    reason: 'Reason / review note',
    number: 'Profile number',
    numberFirst: 'Profile number (first version only)',
    targetType: 'Target type',
    target: 'Target',
    source: 'Source / laboratory / report',
    method: 'Measurement method',
    sampledAt: 'Sampling / measurement time',
    recipe: 'Approved recipe version',
    laboratoryOverride: 'Approved laboratory version with controlled precedence',
    overrideReason: 'Reason for laboratory precedence',
    parameter: 'Analysis parameter',
    value: 'Value',
    unitCode: 'Unit',
    minValue: 'Minimum',
    maxValue: 'Maximum',
    targetValue: 'Target',
    toleranceValue: 'Tolerance',
  },
  parameters: {
    title: 'Configurable analysis parameters',
    subtitle:
      'Industry-neutral parameters; nutrition values are defined only through configurable domains.',
    createTitle: 'Create analysis parameter',
    editTitle: 'Edit analysis parameter v{{version}}',
    editorHelp:
      'All languages are required before review. Units, retention, and rounding control reproducible server calculations.',
    historyTitle: 'Analysis parameter version history',
  },
  calculate: {
    title: 'Calculate analysis values from a recipe',
    description:
      'Resolves sub-recipes and accounts for quantities, losses, yield, absorption, retention, and rounding rules.',
    noOverride: 'Use calculated values only',
    reviewWarning:
      'The result is a draft requiring review. Missing or stale sources are never silently replaced.',
  },
  laboratory: {
    title: 'Record analyzed profile',
    description:
      'Manual values require a traceable source. Upload reports afterwards only to the protected DMS record.',
    values: 'Measurement and analysis values',
    valueHelp: 'Minimum, maximum, target, and tolerance are optional; value and unit are required.',
    dmsHelp:
      'After creation, upload reports in the profile, select them in the draft, and snapshot them with their hashes.',
    editTitle: 'Edit analyzed draft',
    editDescription:
      'Values and DMS selection use optimistic locking. Content becomes immutable after submission.',
  },
  review: {
    title: 'Technical review required',
    description:
      'Calculated values and prioritized laboratory values are not an automatic technical or legal approval. Sources, warnings, and variances must be reviewed.',
  },
  sections: {
    values: 'Approved, calculated, and laboratory values',
    valuesHelp:
      'The approved value is the effective snapshot. For MIXED values, the calculation remains visible for comparison.',
  },
  summary: {
    reference: 'Reference',
    values: 'Parameters',
    errors: 'Errors',
    warnings: 'Warnings',
    validity: 'Validity',
  },
  tabs: {
    values: 'Values',
    sources: 'Provenance',
    validation: 'Validation',
    comparison: 'Comparison',
    documents: 'Reports',
    history: 'Versions',
    snapshot: 'Snapshot',
  },
  sources: {
    manual: 'Manually entered or laboratory-analyzed value',
    noCalculated: 'No automatically calculated material contributions exist for this value.',
  },
  comparison: {
    title: 'Compare versions and laboratory/calculated values',
    description:
      'Values are normalized to compatible units and shown as exact decimal differences.',
  },
  documents: {
    title: 'Protected laboratory and test reports',
    snapshotSelection: 'Snapshot documents for this draft',
    snapshotHelp:
      'Only selected files and their SHA-256 hashes become part of this profile version.',
  },
  history: {
    title: 'Immutable version history',
    description: 'Approved versions remain reproducible despite later source changes.',
  },
  snapshot: { sources: 'Source snapshot', calculation: 'Calculation snapshot' },
  states: {
    loading: 'Loading analysis values …',
    noProfiles: 'No analysis profile version matches the filters.',
    noParameters: 'No analysis parameter version matches the filters.',
    noIssues: 'No validation errors or warnings.',
    notFound: 'The analysis profile version was not found.',
  },
  messages: {
    saved: 'Analysis draft saved.',
    created: 'Laboratory profile created.',
    calculated: 'Reproducible calculation draft created.',
    workflow: 'Analysis workflow updated.',
  },
  workflow: {
    REVIEW: {
      title: 'Document technical review',
      description: 'The reviewer must be independent of the creator and submitter.',
    },
    APPROVE: {
      title: 'Approve analysis profile',
      description: 'Approval freezes values, sources, rules, document hashes, and warnings.',
    },
    COPY: {
      title: 'Copy analyzed version',
      description: 'Creates a new editable draft with full provenance.',
    },
    ARCHIVE: {
      title: 'Archive analysis version',
      description: 'The audit-proof version remains readable and is no longer active.',
    },
  },
} as const;
